import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { HdEphemerisZoomLevel } from './ephemeris-lanes/hd-ephemeris-lanes.models';
import { HD_TRANSIT_LEGEND_CATEGORY_ORDER, HdTransitLegendCategory } from './transit-legend.utils';

/**
 * Scroll-/zoom-staat van de ephemeris-lanes die een vernietiging van het
 * component overleeft. De dock-hosts (`DockAreaComponent`,
 * `DockedPanelsHostComponent`) renderen enkel de áctieve tab via
 * `NgComponentOutlet` — een transits-panel dat zijn actieve state verliest
 * wordt dus vernietigd en bij terugkeer vers gemaakt. De hosts bewaren zelf
 * enkel de scroll van hún content-element (plus omliggende containers); de
 * scrollcontainers bínén het transits-panel (de horizontale lanes-scroll en
 * de verticale panel-wrapper) vallen daarbuiten en worden hier bewaard —
 * het paneel-niveau-equivalent van de `tabScrollPositions` van de hosts.
 */
export interface HdTransitsLanesScrollState {
  /** Zoom-niveau op het moment van bewaren — de px-scroll is enkel op dit niveau coherent. */
  readonly zoom: HdEphemerisZoomLevel;
  /** Horizontale scroll van `.ephemeris-lanes__scroll` (px). */
  readonly lanesScrollLeftPx: number;
  /** Verticale scroll van de panel-wrapper (het `hd-ephemeris-lanes` host-element, px). */
  readonly panelScrollTopPx: number;
  /**
   * Tijdstip (epoch-ms) in het midden van het zichtbare venster op het moment
   * van bewaren. Bij herstel (dock-tab-/view-wissel) wordt dit punt weer in
   * het midden gezet, ongeacht de nieuwe viewport-breedte — zo blijft de
   * oriëntatie behouden. Oude bewaarde staat zonder dit veld valt terug op
   * `lanesScrollLeftPx`.
   */
  readonly centerTimeMs?: number;
}

/**
 * Positie in de verticale dag-flow: de dag bovenaan het beeld en hoe ver
 * (px) de gebruiker binnen die dag gescrold was. Een dagsleutel i.p.v. een
 * px-scroll, want de dag-flow rendert enkel een venster van dagen.
 */
export interface HdTransitsVerticalFlowState {
  readonly focusKey: string;
  readonly offsetPx: number;
}

/**
 * Welke legende-filter: `shared` = de horizontale weergave (Nu & Straks +
 * ephemeris-lanes delen één filter), `vertical` = de verticale dag-flow.
 */
export type HdTransitLegendScope = 'shared' | 'vertical';

/**
 * Zo lang (ms) na het afbreken van de dag-flow geldt zijn bewaarde positie
 * nog: een undock/dock-cyclus of een weergavewissel bouwt de component
 * meteen opnieuw op en houdt de plek; wie het paneel later opnieuw opent,
 * start op vandaag (Steven 2026-09-26).
 */
const VERTICAL_FLOW_STATE_TTL_MS = 15_000;

/**
 * View-staat van de transits-feature die buiten de component-instances leeft:
 *  - de lanes-scroll/zoom (zie {@link HdTransitsLanesScrollState});
 *  - de positie in de verticale dag-flow (kortstondig) en de maand/open-stand
 *    van de kalender (een undock/dock-cyclus herbouwt de weergave);
 *  - de legende-filters per weergave (zie {@link HdTransitLegendScope});
 *  - de verklein-modus van de zwevende transits-view (`panelNarrow`), zodat de
 *    gekozen breedte een undock/dock-cyclus overleeft — spiegelt
 *    `HdDocPanelService.dialogSize` van de doc-viewer.
 */
@Injectable({ providedIn: 'root' })
export class HdTransitsViewStateService {
  private lanesScroll: HdTransitsLanesScrollState | null = null;
  private verticalFlow: { readonly state: HdTransitsVerticalFlowState; readonly savedAtMs: number } | null = null;
  private verticalCalendarMonth: string | null = null;
  private verticalCalendarOpen = false;

  /**
   * Standaard smal (Steven 2026-10-01): losgemaakt uit het rechterpaneel landt
   * de transits-dialog ernaast (`undockPlacement: 'beside-dock'`), zodat het
   * rechterpaneel zichtbaar blijft. Breder maken blijft de hele sessie gelden.
   */
  private readonly panelNarrowSignal = signal(true);
  private readonly floatingViewActiveSignal = signal(false);
  /** De zwevende transits-view is een telefoon-sheet (geen dockarea, geen smal/breed). */
  private readonly floatingViewSheetSignal = signal(false);
  /**
   * Actieve legende-categorieën van de planeten-filter (default alles aan).
   * Sessie-state — gedeeld door het Nu & Straks-panel en de ephemeris-lanes;
   * bewust niet gepersisteerd (enkel view-mode/sort/zoom zijn settings).
   */
  private readonly enabledCategoriesSignal = signal<ReadonlySet<HdTransitLegendCategory>>(
    new Set(HD_TRANSIT_LEGEND_CATEGORY_ORDER),
  );
  /**
   * Eigen filter van de verticale dag-flow, standaard enkel de zon: elke
   * wissel draagt daar een volledige I Tjing-lijnsectie en de maan alleen
   * wisselt ±12-16× per dag van lijn — met alles aan wordt de weergave traag
   * (Steven 2026-09-26). Ook sessie-state.
   */
  private readonly verticalCategoriesSignal = signal<ReadonlySet<HdTransitLegendCategory>>(new Set(['sun']));

  /** `true`: de zwevende transits-view toont smal (~62vw) i.p.v. de volle ~90vw. */
  readonly panelNarrow = this.panelNarrowSignal.asReadonly();

  /**
   * `true` zolang de transits-view als zwevende dialog gemount is (niet gedockt
   * en niet de standalone pagina). Bepaalt samen met {@link panelNarrow} waar
   * detail-popups landen: in de volle breedte overlappen ze als dialog, in de
   * smalle breedte blijft de dockarea ernaast vrij en dokken ze daar.
   */
  readonly floatingViewActive = this.floatingViewActiveSignal.asReadonly();

  /**
   * De zwevende transits-view staat effectief smal: enkel als dialog, nooit als
   * telefoon-sheet (die is altijd volle breedte en heeft geen dockarea ernaast).
   */
  readonly floatingViewNarrow = computed(() => this.panelNarrowSignal() && !this.floatingViewSheetSignal());

  togglePanelNarrow(): void {
    this.panelNarrowSignal.update(narrow => !narrow);
  }

  setFloatingViewActive(active: boolean, sheet = false): void {
    this.floatingViewActiveSignal.set(active);
    this.floatingViewSheetSignal.set(active && sheet);
  }

  saveLanesScroll(state: HdTransitsLanesScrollState): void {
    this.lanesScroll = state;
  }

  /** Laatst bewaarde lanes-scroll, of `null` wanneer er nog geen instance afsluitte. */
  lanesScrollState(): HdTransitsLanesScrollState | null {
    return this.lanesScroll;
  }

  saveVerticalFlowState(state: HdTransitsVerticalFlowState, nowMs: number = Date.now()): void {
    this.verticalFlow = { state, savedAtMs: nowMs };
  }

  /**
   * De bewaarde positie in de dag-flow zolang ze vers is (zie
   * {@link VERTICAL_FLOW_STATE_TTL_MS}); anders `null` → de weergave start op vandaag.
   */
  freshVerticalFlowState(nowMs: number = Date.now()): HdTransitsVerticalFlowState | null {
    const saved = this.verticalFlow;
    if (saved == null || nowMs - saved.savedAtMs > VERTICAL_FLOW_STATE_TTL_MS) return null;
    return saved.state;
  }

  /** Getoonde maand (`YYYY-MM`) van de maandkalender in de verticale weergave. */
  saveVerticalCalendarMonth(monthKey: string): void {
    this.verticalCalendarMonth = monthKey;
  }

  verticalCalendarMonthKey(): string | null {
    return this.verticalCalendarMonth;
  }

  /** Uitgeklapte maandkalender in de smalle weergave. */
  saveVerticalCalendarOpen(open: boolean): void {
    this.verticalCalendarOpen = open;
  }

  isVerticalCalendarOpen(): boolean {
    return this.verticalCalendarOpen;
  }

  /** `true` wanneer de categorie zichtbaar is (aan gevinkt in de filter-legende). */
  isLegendCategoryEnabled(category: HdTransitLegendCategory): boolean {
    return this.enabledCategoriesSignal().has(category);
  }

  /** Signaal-vorm van {@link isLegendCategoryEnabled} voor computed-pipelines. */
  readonly enabledLegendCategories = this.enabledCategoriesSignal.asReadonly();

  toggleLegendCategory(category: HdTransitLegendCategory): void {
    this.toggleLegendCategoryFor('shared', category);
  }

  /** Alle categorieën in één keer aan/uit — de "Selecteer/Deselecteer alle"-optie van de legende. */
  setAllLegendCategories(enabled: boolean): void {
    this.setAllLegendCategoriesFor('shared', enabled);
  }

  /** De actieve categorieën van één weergave (signaal). */
  enabledLegendCategoriesFor(scope: HdTransitLegendScope): Signal<ReadonlySet<HdTransitLegendCategory>> {
    return this.categoriesSignal(scope).asReadonly();
  }

  toggleLegendCategoryFor(scope: HdTransitLegendScope, category: HdTransitLegendCategory): void {
    this.categoriesSignal(scope).update(current => {
      const next = new Set(current);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  setAllLegendCategoriesFor(scope: HdTransitLegendScope, enabled: boolean): void {
    this.categoriesSignal(scope).set(enabled ? new Set(HD_TRANSIT_LEGEND_CATEGORY_ORDER) : new Set());
  }

  private categoriesSignal(scope: HdTransitLegendScope): WritableSignal<ReadonlySet<HdTransitLegendCategory>> {
    return scope === 'vertical' ? this.verticalCategoriesSignal : this.enabledCategoriesSignal;
  }
}
